import {
  buildVariationTable,
  formatQuantity,
  graphWindow,
  parseExpression,
  PointMark,
  type Quantity,
  VariationProblem,
  type VariationResult,
  type VariationTable,
  windowBox,
} from '../expression';
import { type Segment } from '../geometry';
import { type Vec2, vec } from '../math';

/**
 * Drawing a variation table.
 *
 * The model says *what* the table states; this says where every line and every number goes.
 * They are separate files because the arithmetic is worth testing against `x^3-3x` rather than
 * against a coordinate, and because this half has a constraint the other half does not:
 *
 * ## Everything that is not a number is geometry
 *
 * A PDF here is written in `WinAnsiEncoding`, one byte per character, and anything above
 * U+00FF becomes `?`. That rules out `∞`, `↗`, `↘` and `‖` — which is to say, most of what a
 * variation table is made of. A table that reads correctly on screen and prints as a row of
 * question marks is worse than no table, because the failure only shows up on the worksheet.
 *
 * So the arrows are **drawn** — a line with a stroked head, which is what a textbook prints
 * anyway rather than a small glyph — the double bar at a pole is two drawn lines, and infinity
 * is a drawn lemniscate. What remains as text is digits, `+`, `-`, `0` and the row headers,
 * all of which are ASCII. The canvas, the SVG and the PDF then need nothing between them but
 * "stroke these segments, centre this text", so the three cannot disagree.
 *
 * ## Where the values sit
 *
 * A variation table carries its meaning in the *height* of each number: a maximum is written
 * high, a minimum low, and the arrows between them slope accordingly. The height is not stored
 * in the model — it follows from the arrows either side, which is why it is decided here:
 *
 * | arrives | leaves  | the value sits |
 * | ------- | ------- | -------------- |
 * | rising  | falling | high — it is a maximum |
 * | falling | rising  | low — a minimum |
 * | rising  | rising  | in the middle — the curve flattens without turning |
 *
 * At a pole the two one-sided limits are different numbers, so they are written as two cells,
 * each at the height its own arrow demands. That is what makes `1/x` read as falling to −∞ on
 * the left of zero and falling from +∞ on its right.
 */

/** Row heights and column widths, in world units at zoom 1. */
const HEADER_WIDTH = 46;
const ROW_X = 32;
const ROW_DERIVATIVE = 32;
const ROW_VALUE = 96;

/** How far the first and last columns sit inside the frame. */
const EDGE_PAD = 34;

/** Distance between two consecutive columns. */
const STEP_WIDTH = 92;

/** How far a value sits inside the top or bottom of its row. */
const VALUE_INSET = 16;

/**
 * Half the gap between the two cells of a pole.
 *
 * Wide enough for the drawn infinity sign and the `+` in front of it to clear the double bar
 * on both sides — measured against the rendered table rather than guessed, because `+∞` is the
 * widest thing a cell ever holds and it is exactly what a pole puts there.
 */
const POLE_SPREAD = 31;

/** How far an arrow stops short of the value at each end, so it never strikes through it. */
const ARROW_GAP = 20;

const ARROWHEAD = 9;

/** Width of the drawn infinity sign, and how far the sign in front of it sits. */
const INFINITY_WIDTH = 17;
const SIGN_GAP = 13;

/**
 * The table for a graph's formula, over the range that suits it.
 *
 * The choice of range is the whole content of this function, and it lives here — in one
 * place — because two callers need the *same* answer: the evaluator, which builds the table,
 * and the panel, which has to say beforehand whether there will be one. Two copies of the rule
 * would eventually offer a button that then does nothing.
 *
 * The whole line first, because that is the table a textbook prints for `x^3-3x` and the
 * stationary points of a function do not depend on the window a user happens to be looking
 * through. The graph's own range second, and only when the line has too much on it to
 * tabulate: that is what turns `sin(x)` from a refusal into the ordinary table of one arch.
 *
 * `null` when the formula does not parse — a state the caller already knows how to report,
 * and not a fact about variation.
 */
export function variationTableFor(
  expression: string,
  window: { readonly from: number; readonly to: number }
): VariationResult | null {
  let parsed;
  try {
    parsed = parseExpression(expression);
  } catch {
    return null;
  }

  const overTheLine = buildVariationTable(parsed);
  if (overTheLine.ok) return overTheLine;
  if (overTheLine.problem !== VariationProblem.TooManyPoints) return overTheLine;

  return buildVariationTable(parsed, { from: window.from, to: window.to });
}

/** Clear air between the bottom of the figure and the top of the table. */
const TABLE_GAP = 60;

/**
 * Where the table hangs: under everything the figure reaches, aligned with its left edge.
 *
 * ## The defect this replaces
 *
 * The rule was `at = (-half, -half - gap)` where `half` was **half the x range**, on the
 * reasoning — written down at the time — that "a graph's window is as tall as it is wide". It
 * stopped being true when the frame started fitting itself to the curve, and nothing failed:
 * `y = x²` over [−3, 3] is drawn in a frame eight units tall, so a table hung 3 units below the
 * origin came out *inside* the axes, with the y-axis and the numbers −6 and −8 ruled straight
 * through the middle of it. Every entity was correct and the figure was unreadable.
 *
 * ## Why the axes are consulted and not only the curve
 *
 * A script may draw its own `axes 10` around a curve that only needs 3. The frame the reader
 * sees is then the axes, and hanging the table below the *curve* would put it inside them
 * again — the same defect from the other direction. The lower of the two is the bottom of the
 * figure, so that is what it hangs from.
 *
 * The two scales are read separately rather than assumed equal. They are equal in everything
 * built today, and a table that silently moves when that stops being true is exactly the kind
 * of drift this function exists to end.
 *
 * ## And the frame is no longer centred on the origin
 *
 * So "the bottom" is the frame's own lower edge rather than minus its half-height. A parabola
 * framed from 0 to 9 has its bottom at 0, and hanging the table nine units below the axis would
 * leave a hand's width of blank paper between the figure and its table.
 */
export function variationTableAnchor(
  graph: {
    readonly expression: string;
    readonly from: number;
    readonly to: number;
    readonly scale: number;
  },
  axes: {
    readonly extent: number;
    readonly extentY?: number;
    readonly center?: Vec2;
    readonly scale: number;
  } | null
): Vec2 {
  const box = windowBox(graphWindow(graph.expression, graph.from, graph.to));

  const axesLeft = axes === null ? Infinity : ((axes.center?.x ?? 0) - axes.extent) * axes.scale;
  const axesBottom =
    axes === null ? Infinity : ((axes.center?.y ?? 0) - (axes.extentY ?? axes.extent)) * axes.scale;

  return vec.vec2(
    Math.min(box.minX * graph.scale, axesLeft),
    Math.min(box.minY * graph.scale, axesBottom) - TABLE_GAP
  );
}

export interface TableGeometry {
  readonly rules: readonly Segment[];
  readonly marks: readonly { readonly at: Vec2; readonly text: string }[];
  /** The frame, for bounds and for hit testing. */
  readonly corner: Vec2;
  readonly width: number;
  readonly height: number;
}

/**
 * Lays `table` out with its top-left corner at `origin`.
 *
 * Anchored by a corner rather than by its centre because the table is placed *beside*
 * something — a graph, a worked solution — and a corner is what a user lines up.
 */
export function layOutVariationTable(table: VariationTable, origin: Vec2): TableGeometry {
  const count = table.points.length;
  const width = HEADER_WIDTH + EDGE_PAD * 2 + STEP_WIDTH * (count - 1);
  const height = ROW_X + ROW_DERIVATIVE + ROW_VALUE;

  const left = origin.x;
  const right = left + width;
  const top = origin.y;

  // World y points up, so every row below the last is at a *smaller* y.
  const xRowTop = top;
  const xRowBottom = top - ROW_X;
  const derivativeRowBottom = xRowBottom - ROW_DERIVATIVE;
  const valueRowBottom = derivativeRowBottom - ROW_VALUE;

  const columnX = (index: number): number => left + HEADER_WIDTH + EDGE_PAD + STEP_WIDTH * index;

  const rules: Segment[] = [
    // The frame.
    { a: vec.vec2(left, xRowTop), b: vec.vec2(right, xRowTop) },
    { a: vec.vec2(left, valueRowBottom), b: vec.vec2(right, valueRowBottom) },
    { a: vec.vec2(left, xRowTop), b: vec.vec2(left, valueRowBottom) },
    { a: vec.vec2(right, xRowTop), b: vec.vec2(right, valueRowBottom) },
    // Between the rows.
    { a: vec.vec2(left, xRowBottom), b: vec.vec2(right, xRowBottom) },
    { a: vec.vec2(left, derivativeRowBottom), b: vec.vec2(right, derivativeRowBottom) },
    // Between the headers and the table proper.
    {
      a: vec.vec2(left + HEADER_WIDTH, xRowTop),
      b: vec.vec2(left + HEADER_WIDTH, valueRowBottom),
    },
  ];

  const marks: { at: Vec2; text: string }[] = [];

  const headerX = left + HEADER_WIDTH / 2;
  marks.push({ at: vec.vec2(headerX, midpoint(xRowTop, xRowBottom)), text: 'x' });
  marks.push({
    at: vec.vec2(headerX, midpoint(xRowBottom, derivativeRowBottom)),
    // An ASCII apostrophe, not U+2032: the prime is outside Latin-1 and would print as `?`.
    text: "y'",
  });
  marks.push({ at: vec.vec2(headerX, midpoint(derivativeRowBottom, valueRowBottom)), text: 'y' });

  /* ------------------------------------------------------------------ the x row */

  table.points.forEach((point, index) => {
    const at = vec.vec2(columnX(index), midpoint(xRowTop, xRowBottom));
    writeQuantity(rules, marks, point.at, at);
  });

  /* ------------------------------------------------------------- the y' row */

  const derivativeY = midpoint(xRowBottom, derivativeRowBottom);

  table.steps.forEach((step, index) => {
    const between = (columnX(index) + columnX(index + 1)) / 2;
    marks.push({ at: vec.vec2(between, derivativeY), text: step.rising ? '+' : '-' });
  });

  table.points.forEach((point, index) => {
    if (point.mark === PointMark.Zero) {
      marks.push({ at: vec.vec2(columnX(index), derivativeY), text: '0' });
      return;
    }

    if (point.mark === PointMark.Undefined) {
      // The double bar: the function has no value here, and the row is interrupted rather
      // than carrying a symbol that would have to mean the same thing.
      for (const offset of [-3, 3]) {
        rules.push({
          a: vec.vec2(columnX(index) + offset, xRowBottom),
          b: vec.vec2(columnX(index) + offset, valueRowBottom),
        });
      }
    }
  });

  /* -------------------------------------------------------------- the y row */

  const high = derivativeRowBottom - VALUE_INSET;
  const low = valueRowBottom + VALUE_INSET;
  const middle = midpoint(derivativeRowBottom, valueRowBottom);

  /** Where the cell on one side of a point sits, given the arrow on that side. */
  const levelOf = (rising: boolean | null, side: 'in' | 'out'): number => {
    if (rising === null) return middle;
    if (side === 'in') return rising ? high : low;
    return rising ? low : high;
  };

  interface Cell {
    readonly at: Vec2;
    readonly quantity: Quantity;
  }

  /** The cell an arrow leaves from, and the one it arrives at, for every point. */
  const leaving: (Cell | null)[] = [];
  const arriving: (Cell | null)[] = [];

  table.points.forEach((point, index) => {
    const incoming = index === 0 ? null : (table.steps[index - 1]?.rising ?? null);
    const outgoing = index === count - 1 ? null : (table.steps[index]?.rising ?? null);

    const inLevel = levelOf(incoming, 'in');
    const outLevel = levelOf(outgoing, 'out');
    const x = columnX(index);

    if (sameQuantity(point.from, point.to)) {
      /*
       * One value, and the two arrows agree about where it goes — unless they do not, which is
       * exactly the flattening point: the curve rises into it and rises out again, so the
       * value is neither a maximum nor a minimum and belongs in the middle.
       */
      const level =
        point.from === null
          ? outLevel
          : point.to === null
            ? inLevel
            : inLevel === outLevel
              ? inLevel
              : middle;

      // One of the two is always present: only the outer ends have a missing side, and they
      // have the other.
      const quantity = point.from ?? point.to;
      if (quantity === null) {
        arriving.push(null);
        leaving.push(null);
        return;
      }

      const cell: Cell = { at: vec.vec2(x, level), quantity };
      writeQuantity(rules, marks, cell.quantity, cell.at);
      arriving.push(cell);
      leaving.push(cell);
      return;
    }

    // A pole: two limits, each written at the height its own arrow demands.
    const before: Cell | null =
      point.from === null ? null : { at: vec.vec2(x - POLE_SPREAD, inLevel), quantity: point.from };
    const after: Cell | null =
      point.to === null ? null : { at: vec.vec2(x + POLE_SPREAD, outLevel), quantity: point.to };

    if (before !== null) writeQuantity(rules, marks, before.quantity, before.at);
    if (after !== null) writeQuantity(rules, marks, after.quantity, after.at);
    arriving.push(before);
    leaving.push(after);
  });

  table.steps.forEach((_, index) => {
    const from = leaving[index];
    const to = arriving[index + 1];
    if (from === undefined || from === null || to === undefined || to === null) return;
    rules.push(...arrow(from.at, to.at));
  });

  return {
    rules,
    marks,
    corner: vec.vec2(left, top),
    width,
    height,
  };
}

/* ----------------------------------------------------------------------- pieces */

function midpoint(a: number, b: number): number {
  return (a + b) / 2;
}

function sameQuantity(a: Quantity | null, b: Quantity | null): boolean {
  if (a === null || b === null) return true;
  if (a.kind !== b.kind) return false;
  if (a.kind === 'number' && b.kind === 'number') return Math.abs(a.value - b.value) < 1e-9;
  if (a.kind === 'infinity' && b.kind === 'infinity') return a.sign === b.sign;
  return true;
}

/** A value in a cell: a number as text, or infinity as a drawn sign with its sign in front. */
function writeQuantity(
  rules: Segment[],
  marks: { at: Vec2; text: string }[],
  quantity: Quantity,
  at: Vec2
): void {
  switch (quantity.kind) {
    case 'number':
      marks.push({ at, text: formatQuantity(quantity.value) });
      return;

    case 'infinity':
      marks.push({ at: vec.vec2(at.x - SIGN_GAP, at.y), text: quantity.sign > 0 ? '+' : '-' });
      rules.push(...lemniscate(vec.vec2(at.x + 3, at.y), INFINITY_WIDTH));
      return;

    case 'none':
      return;
  }
}

/**
 * The infinity sign, drawn.
 *
 * A lemniscate of Bernoulli, which is the shape the character is, sampled finely enough that
 * it reads as a curve at ordinary zoom. Twenty-four segments is the point at which halving the
 * step stops being visible on screen.
 *
 * It is geometry rather than the character `∞` for one reason: this has to survive being
 * exported to PDF, whose fonts here reach U+00FF and no further.
 */
function lemniscate(centre: Vec2, width: number): readonly Segment[] {
  const a = width / 2;
  const steps = 24;

  const at = (t: number): Vec2 => {
    const denominator = 1 + Math.sin(t) ** 2;
    return vec.vec2(
      centre.x + (a * Math.cos(t)) / denominator,
      centre.y + (a * Math.sin(t) * Math.cos(t)) / denominator
    );
  };

  const segments: Segment[] = [];
  for (let index = 0; index < steps; index++) {
    segments.push({
      a: at((index / steps) * Math.PI * 2),
      b: at(((index + 1) / steps) * Math.PI * 2),
    });
  }
  return segments;
}

/**
 * An arrow between two cells: the shaft, shortened at both ends, and a stroked head.
 *
 * Shortened rather than drawn corner to corner, because a table's arrow runs *between* two
 * numbers and one that reaches all the way strikes through them both.
 */
function arrow(from: Vec2, to: Vec2): readonly Segment[] {
  const away = vec.vec2(to.x - from.x, to.y - from.y);
  const length = Math.hypot(away.x, away.y);
  if (length <= ARROW_GAP * 2) return [];

  const unit = vec.vec2(away.x / length, away.y / length);
  const start = vec.vec2(from.x + unit.x * ARROW_GAP, from.y + unit.y * ARROW_GAP);
  const end = vec.vec2(to.x - unit.x * ARROW_GAP, to.y - unit.y * ARROW_GAP);

  const head = (turn: number): Segment => {
    const angle = Math.atan2(-unit.y, -unit.x) + turn;
    return {
      a: end,
      b: vec.vec2(end.x + Math.cos(angle) * ARROWHEAD, end.y + Math.sin(angle) * ARROWHEAD),
    };
  };

  return [{ a: start, b: end }, head(0.42), head(-0.42)];
}
